import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import { BLOG_POSTS } from "@/data/siteData";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CTABanner } from "@/components/CTABanner";

export default function BlogDetailPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div data-testid="blog-not-found" className="py-32 text-center">
        <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
          Blog post not found
        </h2>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>
      </div>
    );
  }

  // Simple markdown-like parser for bold text
  const renderContent = (content) => {
    return content.split("\n").map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;
      if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
        return (
          <h3
            key={i}
            className="font-heading text-xl font-semibold text-slate-900 mt-8 mb-3"
          >
            {trimmed.replace(/\*\*/g, "")}
          </h3>
        );
      }
      return (
        <p key={i} className="text-slate-600 leading-relaxed mb-4">
          {trimmed}
        </p>
      );
    });
  };

  const otherPosts = BLOG_POSTS.filter((p) => p.slug !== slug);

  return (
    <div data-testid={`blog-detail-${post.slug}`}>
      {/* Header */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="flex w-fit items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All Posts
          </Link>
          <Badge
            variant="secondary"
            className="bg-sky-50 text-sky-600 border-0 text-xs mb-4"
          >
            {post.category}
          </Badge>
          <h1
            data-testid="blog-detail-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6"
          >
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 sm:h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section data-testid="blog-content" className="py-10 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="font-body text-base leading-relaxed">
            {renderContent(post.content)}
          </article>
        </div>
      </section>

      {/* Other Posts */}
      {otherPosts.length > 0 && (
        <section className="py-10 sm:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Separator className="mb-12" />
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8">
              More from the Blog
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="flex gap-4 bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:shadow-md transition-all group"
                >
                  <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm font-semibold text-slate-900 group-hover:text-orange-500 transition-colors mb-1 truncate">
                      {p.title}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {p.excerpt}
                    </p>
                    <span className="text-xs text-slate-400 mt-2 inline-block">
                      {p.date}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABanner />
    </div>
  );
}
